/**
 * The engine's own slice: legs, the active one, and the session machine.
 *
 * Deliberately does NOT hold `record` or anything domain-shaped (txnType,
 * closeReason, tip). This is the file that makes "the flow must not know it is
 * paying for a sale" true — nothing here can spell "bill".
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Leg, ProviderStatus } from "./types";
import {
  LEG,
  LEG_EVENT,
  SESSION,
  SESSION_EVENT,
  isLegInFlight,
  legEventForProviderState,
  nextLegState,
  nextSessionState,
} from "./machine";
import type { SessionState } from "./machine";

const SESSION_KEY = "paymentEngineStore";

export interface EngineState {
  status: SessionState;
  legs: Leg[];
  activeLegId: string | null;
  /**
   * A per-leg action is in flight — refresh, cancel or refund. ONE lock across
   * all legs, not one per leg: `pollStatus(leg, { retry: true })` may RE-RUN the
   * charge rather than read it.
   */
  busyLegId: string | null;
  /** a commit is in flight; guards a second press independently of the button */
  committing: boolean;
  /**
   * What the operator typed with no leg selected, STAMPED with the balance it
   * was typed against — v1 has no such state because its pad is bound to a leg
   * and every path that moves the balance re-seeds that leg
   * (`SplitComponentDesktop:299-308`). Discarded when the balance moves.
   *
   * Lives here rather than in React because it is session state that ACTIONS
   * read: `pick` consults it, and keeping it in the component forced the engine
   * to hold a second, live handle on the store to see it.
   */
  pad: { value: string; balance: number } | null;
}

const initialState: EngineState = {
  status: SESSION.IDLE,
  legs: [],
  activeLegId: null,
  busyLegId: null,
  committing: false,
  pad: null,
};

/**
 * `source` without `keys` — the one place a field is dropped rather than
 * merged. The `Omit` return type stops a later spread putting them back, and
 * naming the keys as arguments leaves no unused bindings behind.
 */
function omit<T extends object, K extends keyof T>(source: T, ...keys: readonly K[]): Omit<T, K> {
  const rest = { ...source } as Record<string, unknown>;
  keys.forEach((key) => delete rest[key as string]);
  return rest as Omit<T, K>;
}

/**
 * Editing is legal only where the SESSION machine allows it, and SETTLING
 * allows nothing (see ./machine — the blank row is the design). This is what
 * makes "edit while a provider holds a leg" unrepresentable rather than merely
 * guarded in the UI.
 */
function canEdit(state: EngineState): boolean {
  return nextSessionState(state.status, SESSION_EVENT.EDIT) !== null;
}

const slice = createSlice({
  name: "paymentEngine",
  initialState,
  reducers: {
    /** `legs` come from `domain.seed(record)` — nothing else is seeded, ever. */
    openSession: (state, action: PayloadAction<{ legs: Leg[] }>) => {
      const resuming = action.payload.legs.some((leg) => isLegInFlight(leg.legState));
      state.status = resuming ? SESSION.SETTLING : SESSION.COLLECTING;
      state.legs = action.payload.legs;
      state.activeLegId = null;
      /**
       * The leg list is REPLACED by the record's, so anything typed against the
       * old one goes with it — a transaction-type switch re-opens through here,
       * and v1 re-arms a fresh leg at the balance on one
       * (`CheckoutHeader.handleChange:130`). The pad's own stamp cannot cover
       * this: it expires on the BALANCE moving, and a switch does not move it.
       *
       * `resync` is the opposite case by design — the record changed under an
       * open session and entered work survives, pad included.
       */
      state.pad = null;
    },

    closeSession: () => initialState,

    /**
     * The record changed under an open session — a discount applied, loyalty
     * redeemed, the bill re-priced. Only legs the RECORD owns are re-seeded;
     * everything the operator has entered survives, which is the whole point.
     */
    resync: (state, action: PayloadAction<{ legs: Leg[] }>) => {
      action.payload.legs.forEach((incoming) => {
        const at = state.legs.findIndex((l) => l.paymentId === incoming.paymentId);
        if (at === -1) state.legs.push(incoming);
      });
    },

    /**
     * ADD PAYMENT — the SPLIT draft joins the bill. `draft` is DELETED, not set
     * to false: a `false` would ride onto the wire.
     */
    registerDraft: (state, action: PayloadAction<string>) => {
      const leg = state.legs.find((l) => l.paymentId === action.payload);
      if (!leg?.draft) return;
      delete leg.draft;
      state.activeLegId = null;
    },

    selectLeg: (state, action: PayloadAction<string | null>) => {
      state.activeLegId = action.payload;
    },

    upsertLeg: (state, action: PayloadAction<Leg>) => {
      if (!canEdit(state)) return; // a leg is with a provider — see ./machine
      const i = state.legs.findIndex((l) => l.paymentId === action.payload.paymentId);
      if (i >= 0) state.legs[i] = action.payload;
      else state.legs.push(action.payload);
      state.activeLegId = action.payload.paymentId;
    },

    patchLeg: (state, action: PayloadAction<{ paymentId: string; patch: Partial<Leg> }>) => {
      if (!canEdit(state)) return;
      const leg = state.legs.find((l) => l.paymentId === action.payload.paymentId);
      if (leg) Object.assign(leg, action.payload.patch);
    },

    /**
     * The PROVIDER's own write onto the leg it is about to be handed — the auth
     * token, the device choice, the validated card. Distinct from `patchLeg`
     * because it runs from `resolve()`, the last step BEFORE `markLegRequested`
     * moves the session to SETTLING.
     */
    resolveLeg: (state, action: PayloadAction<{ paymentId: string; patch: Partial<Leg> }>) => {
      const leg = state.legs.find((l) => l.paymentId === action.payload.paymentId);
      if (!leg || leg.legState !== LEG.DRAFT) return;
      // a provider writes what it LEARNED, never where the leg stands or what it
      // is worth: `markLegRequested` owns the amounts, `applyProviderStatus` the
      // state, and `data` is merged as its own sub-bag below
      const written = action.payload.patch;
      Object.assign(leg, omit(written, "legState", "amount", "requestAmount", "data"));
      /**
       * A provider's blob is its OWN SUB-BAG, and it merges rather than replaces.
       *
       * `leg.data` is the domain's opaque bag and reaches the wire FLAT
       * (`toSaleLeg` spreads it) — `type`, `isCash`, `referenceMandatory`,
       * `pid`, `originalAmount`. v1's provider payload is not one of those: it
       * is a NESTED `payment.data` object every provider writes whole
       * (`pineLabsComp:81` and its seven siblings), and `getDataStruc:875`
       * reads it back as `payment?.data?.deviceId` / `externalPaymentData`.
       *
       * Merged flat, a provider's `mode` collided with the leg's own and its
       * `deviceId` climbed to the top level, so the wire got neither shape.
       */
      if (written.data) {
        leg.data = {
          ...(leg.data ?? {}),
          data: { ...(leg.data?.data ?? {}), ...written.data },
        };
      }
    },

    removeLeg: (state, action: PayloadAction<string>) => {
      if (!canEdit(state)) return;
      const leg = state.legs.find((l) => l.paymentId === action.payload);
      // a posted leg is money already collected — dropping it would quietly
      // shrink the bill (mirrors v1's isSimpleDeletable, CheckoutContainer)
      if (leg?.legState === LEG.POSTED) return;
      state.legs = state.legs.filter((l) => l.paymentId !== action.payload);
      if (state.activeLegId === action.payload) state.activeLegId = null;
    },

    /** REQUESTED — amount moves to `requestAmount` and zeroes, as every provider does it */
    /**
     * v1 has TWO removals and they are not the same rule.
     *
     *   the operator's delete    `removeLeg` — restricted (`canRemoveLeg`), and
     *                            illegal while a provider holds a leg (§12)
     *   the provider-error drop  `resetFunction:1103` — `payments.delete(...)`,
     *                            unrestricted, DURING settling, which is the
     *                            only moment it can happen
     *
     * A poll or refresh that fails at the transport level (`!res || res.code`)
     * removes the leg from the bill entirely. An initiate REJECTION does not —
     * `handleDefaultSelectedError:721` keeps it and marks it failed. Collapsing
     * the two left an undeletable dead leg on the bill after every dropped
     * connection: FAILED legs carry a `paymentStatus`, so `canRemoveLeg` then
     * refuses the operator's delete too.
     *
     * The posted guard is the one invariant neither removal may break: that is
     * money we hold.
     */
    dropLeg: (state, action: PayloadAction<string>) => {
      const leg = state.legs.find((candidate) => candidate.paymentId === action.payload);
      if (!leg || leg.legState === LEG.POSTED) return;
      state.legs = state.legs.filter((candidate) => candidate.paymentId !== action.payload);
      if (state.activeLegId === action.payload) state.activeLegId = null;
      if (!state.legs.some((l) => isLegInFlight(l.legState))) {
        const next = nextSessionState(state.status, "LEG_RESOLVED");
        if (next) state.status = next;
      }
    },

    setBusyLeg: (state, action: PayloadAction<string | null>) => {
      state.busyLegId = action.payload;
    },

    setCommitting: (state, action: PayloadAction<boolean>) => {
      state.committing = action.payload;
    },

    setPad: (state, action: PayloadAction<{ value: string; balance: number } | null>) => {
      state.pad = action.payload;
    },

    markLegRequested: (state, action: PayloadAction<string>) => {
      const leg = state.legs.find((l) => l.paymentId === action.payload);
      if (!leg || leg.legState !== LEG.DRAFT) return;
      const next = nextLegState(leg.legState, "INITIATE");
      if (!next) return;
      leg.legState = next;
      leg.requestAmount = leg.requestAmount ?? leg.amount;
      leg.amount = 0;
      /**
       * A leg a provider now holds is ON THE BILL, whatever SPLIT's draft rule
       * said when it was picked. v1 has the same invariant by construction — it
       * registers externals on pick (`UpdatePaymentLeg:611`) precisely so the
       * response has somewhere to land. Left set, the leg is filtered out of
       * `normalizeLegs`, so the sale posted to the provider would not contain
       * the payment it is being asked to charge.
       */
      delete leg.draft;
      state.activeLegId = leg.paymentId;
      const settlingNext = nextSessionState(state.status, "LEG_REQUESTED");
      if (settlingNext) state.status = settlingNext;
    },

    /**
     * The ONE reducer that moves a leg through the machine from a provider
     * status. Mirrors `applyProviderStatus` in packages/core/payments/session.ts.
     */
    applyProviderStatus: (
      state,
      action: PayloadAction<{
        paymentId: string;
        status: ProviderStatus;
        /** the provider's own allowlist — see `Provider.postedFields` */
        postedFields?: readonly string[];
        remapModeOnPosted?: boolean;
      }>
    ) => {
      const { paymentId, status, postedFields, remapModeOnPosted } = action.payload;
      const leg = state.legs.find((l) => l.paymentId === paymentId);
      if (!leg) return;

      const event = status.code ? "POLL_ERROR" : legEventForProviderState(status.state);
      const next = nextLegState(leg.legState, event);
      if (!next) return;
      leg.legState = next;

      if (next === LEG.POSTED) {
        // the collected amount comes BACK from the provider — it was moved to
        // `requestAmount` and zeroed when the leg was requested
        leg.amount = status.payment?.amount ?? leg.requestAmount ?? 0;
        delete leg.errorMessage;
        delete leg.code;
        if (remapModeOnPosted && status.payment?.mode) leg.mode = status.payment.mode;
        /**
         * An allowlist, but its base is not empty: v1's `handleGetExtCb` copies
         * `reference`, `note` and `transactionId` off the completion response
         * unconditionally, plus whatever the row declares. Read FLAT off
         * `status.payment` and written into `leg.data`, which `toSaleLeg`
         * spreads back to the top level.
         */
        if (status.payment) {
          const from = status.payment as Record<string, any>;
          const bag: Record<string, any> = { ...(leg.data ?? {}) };
          const fields = ["reference", "note", "transactionId", ...(postedFields ?? [])];
          fields.forEach((field) => {
            if (from[field] !== undefined) bag[field] = from[field];
          });
          leg.data = bag;
        }
      } else if (next === LEG.FAILED || next === LEG.CANCELLED) {
        /**
         * v1 has THREE messages, chosen by the provider's state and NOT by
         * what the response says (`handleGetExtCb:1027-1046`):
         *
         *   DECLINED   "Payment declined."
         *   CANCELLED  "Payment cancelled"
         *   anything   res.errorMessage || "Payment failed."
         *
         * Only the last consults the response. Collapsing them told the
         * operator "Payment Failed" for a card the customer declined and for a
         * terminal they cancelled on — three different things to act on.
         */
        leg.errorMessage =
          next === LEG.CANCELLED
            ? "Payment cancelled"
            : event === LEG_EVENT.POLL_DECLINED
              ? "Payment declined."
              : status.errorMessage || status.code || "Payment failed.";
        leg.code = status.code;
        // AFTER any merge above, never before: an earlier version zeroed the
        // amount first and then `Object.assign`ed the whole `status.payment`
        // over it, so a server-echoed amount silently resurrected money on a
        // leg the provider had just declined.
        leg.amount = 0;
        // a declined leg holds no money and cannot be retried in place, so it
        // stops being the selection — in the REDUCER, so it holds even when the
        // auto-save that follows never runs
        if (state.activeLegId === leg.paymentId) state.activeLegId = null;
      }

      // the SESSION resolves only when NOTHING is in flight. Checking just this
      // leg released the session while a second provider still held a payment.
      if (!state.legs.some((l) => isLegInFlight(l.legState))) {
        const sessionNext = nextSessionState(state.status, "LEG_RESOLVED");
        if (sessionNext) state.status = sessionNext;
      }
    },

    applyRefund: (state, action: PayloadAction<{ paymentId: string; status: ProviderStatus }>) => {
      const leg = state.legs.find((l) => l.paymentId === action.payload.paymentId);
      if (!leg || leg.legState !== LEG.POSTED) return;
      // a refund the provider REFUSED moved no money — the leg stays posted, and the amount with it
      if (action.payload.status?.code) return;
      const next = nextLegState(leg.legState, "REFUND");
      if (next) {
        leg.legState = next;
        leg.amount = 0;
      }
    },

    /**
     * Fold the legs `domain.adopt(response)` found in a server response back in.
     *
     * A MERGE BY paymentId, never a replace: a local leg mid-entry is not in the
     * response, and `legState`/`amount` are the MACHINE's — `sale/payment/save`
     * can return a leg already terminal, and taking the wire's word for it
     * leaves money on the bill that nothing can commit. The runner routes those
     * through `applyProviderStatus`.
     */
    adoptLegs: (state, action: PayloadAction<Leg[]>) => {
      action.payload.forEach((incoming) => {
        if (!incoming?.paymentId) return;
        const at = state.legs.findIndex((l) => l.paymentId === incoming.paymentId);
        if (at === -1) {
          state.legs.push(incoming);
          return;
        }
        // only the machine may move a leg, and the wire can report one already
        // terminal — the runner routes those through `applyProviderStatus`
        state.legs[at] = {
          ...state.legs[at],
          ...omit(incoming, "legState", "amount", "data"),
          data: { ...(state.legs[at].data ?? {}), ...(incoming.data ?? {}) },
        };
      });
    },

    commitStarted: (state) => {
      const next = nextSessionState(state.status, "COMMIT");
      if (next) state.status = next;
    },
    commitSucceeded: (state, action: PayloadAction<{ auto?: boolean } | undefined>) => {
      const event = action.payload?.auto ? "SAVED" : "COMMIT_OK";
      const next = nextSessionState(state.status, event);
      if (next) state.status = next;
    },
    commitFailed: (state) => {
      const next = nextSessionState(state.status, "COMMIT_ERR");
      if (next) state.status = next;
    },
  },
});

export const {
  openSession,
  closeSession,
  resync,
  registerDraft,
  selectLeg,
  upsertLeg,
  patchLeg,
  resolveLeg,
  removeLeg,
  dropLeg,
  setBusyLeg,
  setCommitting,
  setPad,
  markLegRequested,
  applyProviderStatus,
  applyRefund,
  adoptLegs,
  commitStarted,
  commitSucceeded,
  commitFailed,
} = slice.actions;

/**
 * A provider's OWN blob off the leg — v1's nested `payment.data`
 * (`pineLabsComp:81`), which `resolveLeg` keeps separate from the flat fields
 * the domain put in `leg.data`. A provider that re-reads what it wrote
 * (`_auth`, gyftr, paytmdqr all merge onto it) must not pick the flat ones up:
 * spreading `pid`/`type`/`isCash` back into its own payload sends the domain's
 * fields to the provider endpoint nested, and to the wire twice.
 */
export function providerData(leg: { data?: Record<string, any> }): Record<string, any> {
  return (leg.data?.data ?? {}) as Record<string, any>;
}

export function selectEngine(rootState: any): EngineState {
  return rootState[SESSION_KEY];
}

export const paymentEngineReducer = slice.reducer;
export { SESSION_KEY };
